// /schedule/auto — Ad Pilot wizard. Single-screen, five-step flow
// (cadence → source → confirmation → platforms → review+schedule)
// living in one column with smooth section transitions; nothing is
// hidden behind a multi-page flow.
//
// Creates an auto_schedule_batches row + N placeholder scheduled_posts
// tiles. For source='generate' tiles sit at mode='auto_generate' until
// the worker renders them; for source='approved' the tiles are
// immediately ready_post. Week-1 generation kicks off in a background
// task on Schedule click so the user's calendar fills the same day;
// week 2+ is handled by the daily cron.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import ProductCatalogPicker, {
  type ProductPickerSelection,
} from '@/components/feature/ProductCatalogPicker';
import {
  createAutoBatch,
  listCompetitors,
  listAutoBatches,
  deleteAutoBatch,
  listProducts,
  type SavedCompetitor,
  type AutoScheduleBatch,
  type UserProduct,
} from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';
import { listPosts, type Post, getSignedImageUrls } from '@/lib/gallery';
import CarouselThumbnail from '@/components/feature/CarouselThumbnail';
import { PLATFORM_META } from '../components/platforms';

/** Platforms supported for Ad Pilot scheduling — limited to those with a
 *  working publish pipeline. TikTok + X live in PLATFORM_PICK_ORDER but
 *  have no OAuth/publish wiring yet, so we hide them here. To re-enable
 *  one: add it to this array AND finish its publish handler in the
 *  backend's _publish_scheduled_to_* dispatch. */
const AD_PILOT_PLATFORMS = ['instagram', 'facebook'] as const;
import { useBrand } from '@/hooks/useBrand';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';
import { startFacebookOAuth } from '@/lib/api';

type Cadence = 1 | 2 | 3 | 5 | 7;
type Source = 'starred' | 'generate' | 'approved';

/** Display rows for the cadence cards. "Recommended" highlights the
 *  default pick. Heuristic for v1: 3×/week is the recommended baseline
 *  per industry studies on growth without follower fatigue. */
const CADENCE_OPTIONS: { value: Cadence; label: string; sub: string; recommended?: boolean }[] = [
  { value: 1, label: '1×/week',  sub: 'Slow & steady — one post per platform' },
  { value: 2, label: '2×/week',  sub: 'Light cadence' },
  { value: 3, label: '3×/week',  sub: 'Steady growth pace', recommended: true },
  { value: 5, label: '5×/week',  sub: 'Weekday daily — high engagement' },
  { value: 7, label: '7×/week',  sub: 'Every day — maximum reach' },
];

/** How long the pilot runs before it ends. */
const WEEKS_OPTIONS: { value: number; label: string; sub: string; recommended?: boolean }[] = [
  { value: 2, label: '2 weeks', sub: 'Short burst' },
  { value: 4, label: '4 weeks', sub: 'A full month', recommended: true },
  { value: 6, label: '6 weeks', sub: 'Sustained push' },
];

/** "Jul 22" from an ISO timestamp (or null). */
function fmtShortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Card sub-label for how the pilot sources its ads. */
function pilotSourceLabel(source: string | undefined): string {
  return source === 'approved' ? 'Using saved posts' : 'Using Ad Strategist';
}

export default function AutoSchedulePage() {
  const navigate = useNavigate();

  // Tablet + desktop only — see the isMobile gate before the render returns.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const { activeBrand } = useBrand();
  const { status: fbStatus } = useFacebookConnection();

  // Connection state — Facebook + Instagram are the publish targets in
  // v1. Without at least one connected, scheduled tiles can be created
  // but the publish-due worker will fail every time and the tiles flip
  // to 'failed'. Warn loudly in Step 4 and hard-block the platform pick.
  const isFbConnected = !!fbStatus?.connected;
  const isIgConnected = !!fbStatus?.connected && !!fbStatus?.instagramUserId;
  const platformAvailable = (id: string): boolean => {
    if (id === 'facebook') return isFbConnected;
    if (id === 'instagram') return isIgConnected;
    return false;
  };
  const handleConnectFacebook = async () => {
    try {
      const { redirectUrl } = await startFacebookOAuth();
      window.location.href = redirectUrl;
    } catch (err) {
      console.error('[AD_PILOT] FB OAuth start failed', err);
    }
  };

  const confirm = useConfirm();

  // ---- View: landing list of pilots vs the create wizard ----
  const [view, setView] = useState<'list' | 'wizard'>('list');
  const [pilots, setPilots] = useState<AutoScheduleBatch[] | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedPilot, setSelectedPilot] = useState<AutoScheduleBatch | null>(null);
  // id → product, to resolve a pilot's frozen product_ids for the read-only view.
  const [productsById, setProductsById] = useState<Map<string, UserProduct>>(new Map());

  // ---- Form state ----
  const [cadence, setCadence] = useState<Cadence>(3);
  const [source, setSource] = useState<Source>('generate');
  const [platforms, setPlatforms] = useState<string[]>(['instagram', 'facebook']);
  // Fixed duration — the pilot runs for exactly this many weeks then ends.
  const [weeksAhead, setWeeksAhead] = useState(4);

  // ---- Data ----
  const [savedCompetitors, setSavedCompetitors] = useState<SavedCompetitor[]>([]);
  const [approvedPosts, setApprovedPosts] = useState<Post[]>([]);
  const [approvedPostIds, setApprovedPostIds] = useState<string[]>([]);
  const [postThumbnails, setPostThumbnails] = useState<Record<string, string>>({});

  // ---- Product picker state (source='generate') ----
  // Selection bubbles up from <ProductCatalogPicker> via onSelectionChange.
  // Owns its own catalog/collections/tab/search state internally; we only
  // see the final {selectedIds, collectionId, allSelected} shape here.
  // Ad Pilot uses the NON-allToggle variant — user must explicitly pick.
  const [pickerSelection, setPickerSelection] = useState<ProductPickerSelection>({
    selectedIds: [],
    collectionId: null,
    allSelected: false,
  });

  // ---- Submit ----
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---- Load reference data on mount ----
  // Catalog + collections are now loaded inside <ProductCatalogPicker>.
  // We only fetch what the picker doesn't: competitors + approved posts.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [competitors, posts] = await Promise.all([
          listCompetitors()
            .then((r) => r.competitors)
            .catch(() => [] as SavedCompetitor[]),
          listPosts(activeBrand?.id ?? null).catch(() => []),
        ]);
        if (cancelled) return;

        // Saved competitors from /api/competitors — Ad Pilot uses the
        // full library (no cap). The worker hits each weekly + caches in
        // ad_intel_cache, so cost scales gracefully with list size.
        setSavedCompetitors(competitors);

        // Approved + non-zip (carousels are awkward to schedule as 4 separate frames in v1).
        const approved = posts.filter(
          (p) => (p as Post & { approved?: boolean }).approved !== false,
        );
        setApprovedPosts(approved);

        // Thumbnail URLs for EVERY visible post (not just the first 12 —
        // that bug left rows 13-24 stuck on a pulsing placeholder).
        // Post-GCS-migration `getSignedImageUrls` is just a synchronous
        // public-URL builder, so calling it on the whole list is cheap.
        // Jul 14 — carousels (.zip) get RAW public URLs (no image transform —
        // it can't resize a zip); CarouselThumbnail fetches + unzips them for a
        // real 2×2 preview. Stub rows (no image_path) are skipped.
        const imgSlice = approved.filter(
          (p) => p.image_path && !p.image_path.endsWith('.zip'),
        );
        const zipSlice = approved.filter(
          (p) => p.image_path && p.image_path.endsWith('.zip'),
        );
        try {
          const [imgUrls, zipUrls] = await Promise.all([
            getSignedImageUrls(imgSlice.map((p) => p.image_path), 3600, { width: 240, quality: 75 }),
            getSignedImageUrls(zipSlice.map((p) => p.image_path), 3600),
          ]);
          if (cancelled) return;
          const thumbs: Record<string, string> = {};
          for (const p of imgSlice) {
            if (imgUrls[p.image_path]) thumbs[p.id] = imgUrls[p.image_path];
          }
          for (const p of zipSlice) {
            if (zipUrls[p.image_path]) thumbs[p.id] = zipUrls[p.image_path];
          }
          setPostThumbnails(thumbs);
        } catch { /* fallback handled in JSX */ }
      } catch (err) {
        console.error('[auto-schedule] load failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeBrand?.id]);

  // ---- Ad Pilot list (landing) ----
  const loadPilots = useCallback(async () => {
    try {
      const list = await listAutoBatches();
      setPilots(list);
    } catch (err) {
      console.error('[ad-pilot] list failed', err);
      setPilots([]);
    }
  }, []);

  useEffect(() => { void loadPilots(); }, [loadPilots, activeBrand?.id]);

  // Products (for resolving a pilot's frozen product_ids in the read-only view).
  useEffect(() => {
    void listProducts()
      .then((list) => setProductsById(new Map(list.map((p) => [p.id, p]))))
      .catch(() => { /* read-only view falls back to a count */ });
  }, [activeBrand?.id]);

  const handleDeletePilot = useCallback(async (p: AutoScheduleBatch) => {
    const running = p.status === 'active';
    const ok = await confirm({
      title: running ? 'Stop this Ad Pilot?' : 'Remove this Ad Pilot?',
      message: running
        ? 'This stops future generation and removes any upcoming ads that haven’t rendered yet. Ads already on your calendar stay.'
        : 'This removes the pilot from your list. Ads already on your calendar stay.',
      confirmLabel: running ? 'Stop & remove' : 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setDeletingId(p.id);
    try {
      await deleteAutoBatch(p.id);
      setPilots((cur) => (cur ? cur.filter((x) => x.id !== p.id) : cur));
    } catch (err) {
      console.error('[ad-pilot] delete failed', err);
    } finally {
      setDeletingId(null);
    }
  }, [confirm]);

  // ---- Derived ----
  const competitorUrls = useMemo<string[]>(() => {
    // Full saved competitor library for this brand. The worker uses the
    // same list — `_fetch_user_competitors(user_id, brand_id)` in main.py.
    // No cap (user lock, June 8).
    return savedCompetitors.map((c) => c.query).filter((q): q is string => !!q);
  }, [savedCompetitors]);

  /** Authoritative product selection for the backend submit. Mirrors what
   *  the embedded picker reports on every change. */
  const selectedProductIds = pickerSelection.selectedIds;
  const sendCollectionId = pickerSelection.collectionId;

  /** Starred ads (posts.starred=true) that are actually renderable (have an
   *  image_path — a starred stub can't be scheduled). The "Use my starred ads"
   *  source auto-cycles this whole set (reuses the backend 'approved' path). */
  const starredPosts = useMemo(
    () => approvedPosts.filter((p) => p.starred && !!p.image_path),
    [approvedPosts],
  );

  /** Number of distinct rendered ads (multi-platform tiles for the same
   *  slot share one render). cadence × weeks_ahead. */
  const adsPerBatch = cadence * weeksAhead;
  /** Total scheduled tiles (ads fanned across platforms). cadence ×
   *  weeks × platforms. */
  const tilesPreview = adsPerBatch * platforms.length;

  // Only count platforms that are ACTUALLY connected — otherwise the
  // submit can succeed but the publish-due worker would fail every tile.
  const connectedSelected = platforms.filter(platformAvailable);
  const canSubmit =
    !submitting &&
    connectedSelected.length > 0 &&
    (
      (source === 'starred' && starredPosts.length > 0) ||
      (source === 'generate' && selectedProductIds.length > 0) ||
      (source === 'approved' && approvedPostIds.length > 0)
    );

  // Auto-prune selected platforms that flip from connected→disconnected
  // (e.g. user disconnects FB from another tab while this wizard is open).
  useEffect(() => {
    setPlatforms((prev) => {
      const filtered = prev.filter(platformAvailable);
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [isFbConnected, isIgConnected]);

  // ---- Handlers ----
  const togglePlatform = (id: string) => {
    setPlatforms((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };
  const toggleApproved = (id: string) => {
    setApprovedPostIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };
  // Select all / clear (Jul 14) — only the pickable (rendered, non-stub) posts.
  const selectableApprovedIds = approvedPosts
    .filter((p) => p.image_path)
    .map((p) => p.id);
  const allApprovedSelected =
    selectableApprovedIds.length > 0 &&
    selectableApprovedIds.every((id) => approvedPostIds.includes(id));
  const toggleSelectAllApproved = () =>
    setApprovedPostIds(allApprovedSelected ? [] : selectableApprovedIds);

  /** Stash the latest picker selection. Stable identity via useCallback
   *  so the picker doesn't re-fire its onSelectionChange effect on every
   *  parent render. */
  const handlePickerChange = useCallback((next: ProductPickerSelection) => {
    setPickerSelection(next);
  }, []);

  const handleSubmit = useCallback(async () => {
    setError(null);
    setSubmitting(true);
    try {
      // "Use my starred ads" reuses the backend 'approved' cycle path — we
      // just auto-fill the pool with the whole starred set (no manual pick).
      const effectiveSource = source === 'starred' ? 'approved' : source;
      const effectiveApprovedPostIds =
        source === 'starred'
          ? starredPosts.map((p) => p.id)
          : source === 'approved'
            ? approvedPostIds
            : undefined;
      const result = await createAutoBatch({
        cadencePerWeek: cadence,
        platforms,
        source: effectiveSource,
        competitorUrls: source === 'generate' ? competitorUrls : undefined,
        weeksAhead,
        approvedPostIds: effectiveApprovedPostIds,
        collectionId: source === 'generate' ? sendCollectionId : undefined,
        productIds: source === 'generate' ? selectedProductIds : undefined,
        userOffsetMinutes: new Date().getTimezoneOffset(),
      });
      // Hand off to the calendar — passes a one-shot success toast hint
      // via router state. /schedule's mount effect reads + clears it so
      // a refresh doesn't show the toast twice. The week-1 worker is
      // already running in the background; tiles populate as renders
      // complete (calendar polls while any auto_generate tile is empty).
      navigate('/schedule', {
        state: {
          adPilotJustScheduled: {
            tilesCreated: result.tiles_created,
            adsPerBatch: cadence * weeksAhead,
            source: effectiveSource,
          },
        },
        replace: true,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Try again.';
      setError(msg);
      setSubmitting(false);
    }
  }, [cadence, platforms, source, competitorUrls, weeksAhead, approvedPostIds, starredPosts, sendCollectionId, selectedProductIds, navigate]);

  // Ad Pilot is tablet + desktop only — the wizard's multi-step layout +
  // pickers don't fit a phone. Gate below md (768px).
  if (isMobile) {
    return (
      <AppLayoutV2>
        <div className="min-h-[calc(100dvh-4rem)] flex flex-col items-center justify-center px-8 py-12 text-center">
          <i className="ri-computer-line text-5xl text-primary/40 mb-4" />
          <h1 className="text-lg font-bold text-dark mb-1.5">Ad Pilot needs a bigger screen</h1>
          <p className="text-sm text-dark-muted max-w-xs leading-relaxed mb-5">
            Setting up an automated ad schedule needs the wizard&apos;s wider
            layout. Open this on a tablet or desktop to use Ad Pilot.
          </p>
          <button
            type="button"
            onClick={() => navigate('/create')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors cursor-pointer shadow-md shadow-primary/25"
          >
            <i className="ri-arrow-left-line" />
            Back to create
          </button>
        </div>
      </AppLayoutV2>
    );
  }

  // ---- Render: read-only pilot detail (same layout as the wizard, selected
  // values only, grayed + view-only) ----
  if (selectedPilot) {
    const p = selectedPilot;
    const running = p.status === 'active';
    const pct = Math.max(0, Math.min(100, p.progress_pct ?? 0));
    const cadenceOpt = CADENCE_OPTIONS.find((o) => o.value === p.cadence_per_week);
    const weeksOpt = WEEKS_OPTIONS.find((o) => o.value === p.weeks_ahead);
    const sourceGenerate = (p.source ?? 'generate') === 'generate';
    const pilotProducts = (p.product_ids ?? [])
      .map((id) => productsById.get(id))
      .filter((x): x is UserProduct => !!x);
    const statusLabel = running ? 'Running' : p.status === 'cancelled' ? 'Stopped' : 'Ended';

    return (
      <AppLayoutV2>
        <div className="max-w-[1500px] mx-auto px-6 py-8">
          {/* Header */}
          <button
            onClick={() => setSelectedPilot(null)}
            className="inline-flex items-center gap-1 text-xs font-bold text-dark-muted hover:text-primary mb-2 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" /> Back to pilots
          </button>
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-flight-takeoff-line text-primary text-2xl" />
                <h1 className="font-display font-bold text-dark text-2xl">Ad Pilot</h1>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                    running
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                      : 'bg-surface-dark/30 border border-surface-dark/40 text-dark-muted'
                  }`}
                >
                  {running && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                  {statusLabel}
                </span>
              </div>
              <p className="text-dark-muted text-sm">
                Read-only — this is what the pilot is running. {pct}% completed.
                {' '}To change it, stop it and start a new one.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setSelectedPilot(null); void handleDeletePilot(p); }}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold cursor-pointer"
            >
              <i className="ri-delete-bin-line" /> {running ? 'Stop & remove' : 'Remove'}
            </button>
          </div>

          {/* Read-only sections — same layout, selected values only. */}
          <Section number={1} title="How often should you post?" icon="ri-rhythm-line">
            <div className="grid sm:grid-cols-2 gap-2">
              <ReadOnlyCard title={cadenceOpt?.label ?? `${p.cadence_per_week}×/week`} sub={cadenceOpt?.sub} />
              <ReadOnlyCard title={weeksOpt?.label ?? `${p.weeks_ahead} weeks`} sub={weeksOpt?.sub ?? 'Duration'} />
            </div>
          </Section>

          <Section number={2} title="Where should the content come from?" icon="ri-stack-line">
            <ReadOnlyCard
              icon={sourceGenerate ? 'ri-sparkling-2-line' : 'ri-check-double-line'}
              title={sourceGenerate ? 'Use Ad Strategist to generate ads weekly' : 'Use my saved posts'}
              sub={sourceGenerate
                ? 'Each week spawns an Ad Strategist run against your competitors.'
                : 'Cycles through your approved posts from My Creatives.'}
            />
          </Section>

          {sourceGenerate && (
            <Section number={3} title="Products featured" icon="ri-shopping-bag-3-line">
              {pilotProducts.length > 0 ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                  {pilotProducts.map((prod) => (
                    <div key={prod.id} className="rounded-xl border border-surface-dark/30 bg-surface/40 overflow-hidden opacity-90">
                      <div className="aspect-square bg-white flex items-center justify-center">
                        {prod.imageUrl && (
                          <img src={prod.imageUrl} alt="" className="w-full h-full object-contain p-2" loading="lazy" />
                        )}
                      </div>
                      <p className="px-2 py-1.5 text-[11px] font-semibold text-dark-muted line-clamp-2 leading-tight">
                        {prod.name || 'Product'}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-dark-muted">
                  {(p.product_ids?.length ?? 0)} product{(p.product_ids?.length ?? 0) === 1 ? '' : 's'} featured.
                </p>
              )}
            </Section>
          )}

          <Section number={sourceGenerate ? 4 : 3} title="Platforms" icon="ri-share-line">
            <div className="flex flex-wrap gap-2">
              {(p.platforms || []).map((id) => {
                const m = PLATFORM_META[id];
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-surface-dark/30 bg-surface/40 text-sm font-semibold text-dark-muted capitalize opacity-90"
                  >
                    {m?.icon && <i className={m.icon} style={{ color: m.color }} />}
                    {m?.label || id}
                  </span>
                );
              })}
            </div>
          </Section>

          <div className="mt-4">
            <button
              type="button"
              onClick={() => navigate('/schedule')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer shadow-md shadow-primary/25"
            >
              <i className="ri-calendar-line" /> View on calendar
            </button>
          </div>
        </div>
      </AppLayoutV2>
    );
  }

  // ---- Render: landing list of pilots ----
  if (view === 'list') {
    return (
      <AppLayoutV2>
        <div className="max-w-[1500px] mx-auto px-6 py-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <i className="ri-flight-takeoff-line text-primary text-2xl" />
                <h1 className="font-display font-bold text-dark text-2xl">Ad Pilot</h1>
              </div>
              <p className="text-dark-muted text-sm">
                Set a cadence + duration and Ad Pilot fills your calendar automatically,
                week after week. Track your running pilots below.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setView('wizard')}
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold cursor-pointer shadow-md shadow-primary/25"
            >
              <i className="ri-add-line text-base" /> New Ad Pilot
            </button>
          </div>

          {pilots === null ? (
            <div className="flex items-center gap-3 px-4 py-6 text-dark-muted">
              <i className="ri-loader-4-line animate-spin text-lg" /> Loading your pilots&hellip;
            </div>
          ) : pilots.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-surface-dark/50 bg-white/60 px-6 py-12 text-center">
              <i className="ri-flight-takeoff-line text-primary/50 text-4xl" />
              <p className="mt-3 font-display font-bold text-dark">No pilots yet</p>
              <p className="text-sm text-dark-muted mt-1">
                Hit <strong>New Ad Pilot</strong> to schedule ads automatically for the next few weeks.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-dark-muted mb-2">
                Your pilots
              </p>
              {pilots.map((p) => {
                const running = p.status === 'active';
                const pct = Math.max(0, Math.min(100, p.progress_pct ?? 0));
                const until = fmtShortDate(p.end_date);
                const busy = deletingId === p.id;
                return (
                  <div
                    key={p.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedPilot(p)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedPilot(p); } }}
                    className="flex items-center gap-4 px-5 py-4 rounded-2xl bg-white border border-surface-dark/40 hover:border-primary/40 hover:shadow-md transition-all cursor-pointer"
                  >
                    {/* Icon tile — matches the Ad Strategist list rows */}
                    <div className="w-11 h-11 rounded-xl bg-primary-light/60 flex items-center justify-center text-primary shrink-0">
                      <i className="ri-flight-takeoff-line text-xl" />
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* Line 1 — status badge + title */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                            running
                              ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                              : 'bg-surface-dark/30 border border-surface-dark/40 text-dark-muted'
                          }`}
                        >
                          {running ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                              Running
                            </>
                          ) : (
                            <>
                              <i className="ri-flag-line text-[11px]" />
                              {p.status === 'cancelled' ? 'Stopped' : 'Ended'}
                            </>
                          )}
                        </span>
                        <p className="font-display font-bold text-dark text-base truncate">
                          {p.cadence_per_week}×/week
                          {until && <> · Until {until}</>}
                          {' · '}{pilotSourceLabel(p.source)}
                        </p>
                      </div>

                      {/* Line 2 — progress bar + label, grouped + spaced */}
                      <div className="mt-2.5 flex items-center gap-3">
                        <div className="h-2 flex-1 max-w-sm rounded-full bg-surface-dark/25 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${running ? 'bg-primary' : 'bg-dark-muted/50'}`}
                            style={{ width: `${Math.max(pct, running ? 2 : 0)}%` }}
                          />
                        </div>
                        <span className="text-xs font-bold text-dark-muted shrink-0">{pct}% completed</span>
                      </div>
                    </div>

                    {/* Stop / delete — stopPropagation so it doesn't open the pilot */}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); void handleDeletePilot(p); }}
                      disabled={busy}
                      title={running ? 'Stop & remove this pilot' : 'Remove this pilot'}
                      aria-label={running ? 'Stop and remove pilot' : 'Remove pilot'}
                      className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-dark-muted hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40"
                    >
                      {busy ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-delete-bin-line text-lg" />}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </AppLayoutV2>
    );
  }

  // ---- Render: create wizard ----
  return (
    <AppLayoutV2>
      <div className="max-w-[1500px] mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => setView('list')}
            className="inline-flex items-center gap-1 text-xs font-bold text-dark-muted hover:text-primary mb-2 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" /> Back to pilots
          </button>
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-flight-takeoff-line text-primary text-2xl" />
            <h1 className="font-display font-bold text-dark text-2xl">Ad Pilot</h1>
          </div>
          <p className="text-dark-muted text-sm">
            Fill your calendar in one click. Pick a cadence, choose how content gets
            sourced, and we'll handle the rest week after week.
          </p>
        </div>

        {/* Step 1 — Cadence */}
        <Section number={1} title="How often should you post?" icon="ri-rhythm-line">
          <div className="grid sm:grid-cols-2 gap-2">
            {CADENCE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setCadence(opt.value)}
                className={`relative text-left rounded-xl border-2 px-4 py-3 transition-all cursor-pointer ${
                  cadence === opt.value
                    ? 'border-primary bg-primary/5'
                    : 'border-surface-dark/30 bg-white hover:border-primary/40'
                }`}
              >
                {opt.recommended && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
                    Recommended
                  </span>
                )}
                <div className="font-display font-bold text-dark text-base">{opt.label}</div>
                <div className="text-xs text-dark-muted mt-0.5">{opt.sub}</div>
              </button>
            ))}
          </div>

          {/* Duration — the pilot runs for a fixed number of weeks then ends. */}
          <div className="mt-5 pt-4 border-t border-surface-dark/30">
            <p className="text-sm font-bold text-dark mb-2">For how long?</p>
            <div className="grid grid-cols-3 gap-2">
              {WEEKS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setWeeksAhead(opt.value)}
                  className={`relative text-left rounded-xl border-2 px-4 py-3 transition-all cursor-pointer ${
                    weeksAhead === opt.value
                      ? 'border-primary bg-primary/5'
                      : 'border-surface-dark/30 bg-white hover:border-primary/40'
                  }`}
                >
                  {opt.recommended && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold uppercase tracking-wide">
                      Recommended
                    </span>
                  )}
                  <div className="font-display font-bold text-dark text-base">{opt.label}</div>
                  <div className="text-xs text-dark-muted mt-0.5">{opt.sub}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-dark-muted mt-2">
              Posts {cadence}× a week for {weeksAhead} weeks — {cadence * weeksAhead} ads total. The
              pilot ends automatically when the run is done.
            </p>
          </div>
        </Section>

        {/* Step 2 — Source */}
        <Section number={2} title="Where should the content come from?" icon="ri-stack-line">
          <div className="space-y-2">
            <SourceCard
              active={source === 'starred'}
              onClick={() => setSource('starred')}
              icon="ri-star-line"
              title="Use my starred ads"
              body={
                starredPosts.length === 0
                  ? 'No starred ads yet — tap the ★ on ads in My Creatives to add them here.'
                  : `Cycle all ${starredPosts.length} of your starred ${starredPosts.length === 1 ? 'ad' : 'ads'} from My Creatives, in order.`
              }
              disabled={starredPosts.length === 0}
            />
            <SourceCard
              active={source === 'generate'}
              onClick={() => setSource('generate')}
              icon="ri-radar-line"
              title="Use Ad Strategist to generate ads weekly"
              body={
                competitorUrls.length > 0
                  ? `Each week we spawn an Ad Strategist run against your competitors and use its winning ideas. Find the run in Previous analyses → "Ad Pilot · Week of …".`
                  : "Each week we spawn an Ad Strategist run (brand-only mode, since you haven't added competitors) and use its winning ideas. Add competitors to counter what's working in your category."
              }
            />
            <SourceCard
              active={source === 'approved'}
              onClick={() => setSource('approved')}
              icon="ri-check-double-line"
              title="Use my saved posts"
              body={
                approvedPosts.length === 0
                  ? 'No saved posts yet. Save some from the wizard or Ad Strategist first.'
                  : `Cycle through ${approvedPosts.length} approved posts from My Creatives.`
              }
              disabled={approvedPosts.length === 0}
            />
          </div>
        </Section>

        {/* Step 3 — starred-ad preview (read-only) | product picker (generate) | post picker (approved) */}
        {source === 'starred' && (
          <Section number={3} title="These starred ads will be scheduled" icon="ri-star-line">
            <div className="min-h-[440px] flex flex-col">
              <p className="text-xs text-dark-muted mb-2 flex-shrink-0">
                All {starredPosts.length} of your starred ads will be cycled in order across your schedule.
                Star or unstar ads in My Creatives to change this set.
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto pr-1">
                {starredPosts.map((p) => {
                  const isCarousel = p.image_path.endsWith('.zip');
                  const url = postThumbnails[p.id];
                  return (
                    <div
                      key={p.id}
                      className="relative aspect-square rounded-xl overflow-hidden border-2 border-primary/40"
                      title={p.title || p.headline || 'Ad'}
                    >
                      {isCarousel && url ? (
                        <CarouselThumbnail zipUrl={url} alt={p.title || 'carousel'} className="absolute inset-0 w-full h-full" />
                      ) : url ? (
                        <img src={url} alt={p.title || 'ad'} className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                      ) : (
                        <div className="absolute inset-0 bg-surface-dark/30 animate-pulse" />
                      )}
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
                        <i className="ri-star-fill text-[10px]" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Section>
        )}
        {source === 'generate' && (
          <Section number={3} title="Pick products to feature" icon="ri-shopping-bag-3-line">
            <ProductCatalogPicker
              includeAllToggle={false}
              includeCollections={true}
              onSelectionChange={handlePickerChange}
            />
          </Section>
        )}

        {source === 'approved' && (
          <Section number={3} title="Pick which posts to cycle through" icon="ri-image-line">
            {approvedPosts.length === 0 ? (
              <div className="rounded-xl border border-dashed border-surface-dark/40 bg-surface/40 px-5 py-6 text-center min-h-[440px] flex flex-col items-center justify-center">
                <i className="ri-image-line text-2xl text-dark-muted/60" />
                <p className="text-sm font-semibold text-dark mt-2">No saved posts yet</p>
                <p className="text-xs text-dark-muted mt-0.5">
                  Save a post from the wizard or Ad Strategist first.
                </p>
              </div>
            ) : (
              // Container matches Pick Products' min-h-[440px] so swapping
              // sources doesn't make the panel jump in height.
              <div className="min-h-[440px] flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-2 flex-shrink-0">
                  <p className="text-xs text-dark-muted">
                    Pick at least 1. We'll cycle them in order across your schedule.
                  </p>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button
                      type="button"
                      onClick={toggleSelectAllApproved}
                      className="text-xs font-bold text-primary hover:text-primary-hover whitespace-nowrap cursor-pointer"
                    >
                      {allApprovedSelected ? 'Clear all' : 'Select all'}
                    </button>
                    <span className="text-xs text-primary font-bold whitespace-nowrap">
                      {approvedPostIds.length} selected · {approvedPosts.length} total
                    </span>
                  </div>
                </div>
                {/* Grid identical to ProductCatalogPicker: 3→6 columns, 2-gap,
                    aspect-square tiles, max-h-[400px] scroll. ALL approved
                    posts are rendered (was previously capped at 24, which
                    silently dropped the rest). */}
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto pr-1">
                  {approvedPosts.map((p) => {
                    const isCarousel = p.image_path.endsWith('.zip');
                    const isStub = !p.image_path;
                    const url = postThumbnails[p.id];
                    const active = approvedPostIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => !isStub && toggleApproved(p.id)}
                        disabled={isStub}
                        className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                          isStub
                            ? 'border-surface-dark/30 opacity-40 cursor-not-allowed'
                            : active
                              ? 'border-primary shadow-md shadow-primary/20 cursor-pointer'
                              : 'border-surface-dark/30 hover:border-primary/40 cursor-pointer'
                        }`}
                        title={p.title || p.headline || 'Post'}
                      >
                        {isCarousel && url ? (
                          <CarouselThumbnail
                            zipUrl={url}
                            alt={p.title || 'carousel'}
                            className="absolute inset-0 w-full h-full"
                          />
                        ) : isCarousel ? (
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center text-dark-muted">
                            <i className="ri-stack-line text-2xl" />
                          </div>
                        ) : isStub ? (
                          <div className="absolute inset-0 bg-surface flex flex-col items-center justify-center text-dark-muted gap-1">
                            <i className="ri-lock-line text-lg" />
                            <span className="text-[9px] font-bold uppercase tracking-wide">Locked</span>
                          </div>
                        ) : url ? (
                          <img
                            src={url}
                            alt={p.title || 'post'}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="absolute inset-0 bg-surface-dark/30 animate-pulse" />
                        )}
                        {active && (
                          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
                            <i className="ri-check-line text-xs" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </Section>
        )}

        {/* Step 4 — Platforms */}
        <Section number={4} title="Which platforms?" icon="ri-share-line">
          {/* Connection warning — surfaces when neither IG nor FB is
              connected. Scheduled tiles would otherwise be created but
              the publish worker would fail every one of them. */}
          {!isFbConnected && !isIgConnected && (
            <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm flex items-start gap-2 text-amber-900">
              <i className="ri-error-warning-line mt-0.5" />
              <div className="flex-1">
                <strong>Connect Facebook + Instagram first.</strong>
                {' '}Ad Pilot can schedule placeholders on the calendar,
                but the publish worker will fail every one of them until
                an account is connected.
                <button
                  type="button"
                  onClick={handleConnectFacebook}
                  className="ml-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-900 text-amber-50 text-xs font-bold hover:bg-amber-950 transition-colors cursor-pointer"
                >
                  <i className="ri-facebook-circle-fill" />
                  Connect now
                </button>
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {AD_PILOT_PLATFORMS.map((id) => {
              const m = PLATFORM_META[id];
              const active = platforms.includes(id);
              const available = platformAvailable(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => available ? togglePlatform(id) : void handleConnectFacebook()}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold transition-all cursor-pointer border ${
                    !available
                      ? 'border-surface-dark/30 bg-surface/40 text-dark-muted opacity-60 hover:opacity-100 hover:border-amber-400'
                      : active
                        ? 'border-primary bg-primary/5 text-dark'
                        : 'border-surface-dark/30 bg-white text-dark-muted hover:border-primary/40'
                  }`}
                >
                  <i className={m.icon} style={{ color: m.color }} />
                  {m.label}
                  {!available && (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold ml-0.5">
                      <i className="ri-link" />
                      Connect
                    </span>
                  )}
                  {available && active && <i className="ri-check-line text-primary text-xs ml-0.5" />}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Step 5 — Confirm */}
        <Section number={5} title="Ready to fill the calendar?" icon="ri-calendar-event-line">
          <div className="rounded-xl bg-primary/5 border border-primary/20 px-4 py-3 mb-3">
            <div className="font-display font-bold text-dark text-base">
              We'll schedule {tilesPreview} post{tilesPreview === 1 ? '' : 's'}
            </div>
            <div className="text-xs text-dark-muted mt-1 leading-snug">
              {source === 'generate' ? (
                <>
                  <strong className="text-dark">{adsPerBatch} unique ad{adsPerBatch === 1 ? '' : 's'}</strong>
                  {' '}({cadence}×/week × {weeksAhead} weeks)
                  {' '}× <strong className="text-dark">{platforms.length || 0} platform{platforms.length === 1 ? '' : 's'}</strong>
                  {' '}= {tilesPreview} scheduled post{tilesPreview === 1 ? '' : 's'}
                </>
              ) : (
                <>{cadence}×/week × {platforms.length || 0} platform{platforms.length === 1 ? '' : 's'} × {weeksAhead} weeks</>
              )}
            </div>
            {source === 'generate' && (
              <div className="mt-2 pt-2 border-t border-primary/20 text-xs text-dark-muted leading-snug">
                <span className="font-bold text-dark">Credit cost per ad</span>
                {' '}— single = 1 credit, carousel = 4 credits (1 per frame).
                AI picks the format per concept, so total spend depends on the mix.
              </div>
            )}
            {source === 'approved' && (
              <div className="mt-2 pt-2 border-t border-primary/20 flex items-center gap-2 text-xs">
                <i className="ri-checkbox-circle-line text-emerald-600" />
                <span className="text-dark-muted">
                  <strong className="text-emerald-700">0 credits</strong>
                  {' '}— cycling your saved posts (no rendering)
                </span>
              </div>
            )}
          </div>

          {/* Helpful bullets — week-1 immediate kickoff + weekly refresh +
              best-time posting. Replaced the dropped standalone Section 3
              confirmation card; lives here so users see it right before
              they commit. */}
          {source === 'generate' && (
            <div className="rounded-xl border border-surface-dark/30 bg-surface/30 p-3 mb-3">
              <ul className="space-y-1.5 text-xs text-dark">
                <li className="flex items-start gap-2">
                  <i className="ri-rocket-2-line text-primary text-sm flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>This week's ads start generating immediately</strong> — they'll show up in My Creatives within a few minutes.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-refresh-line text-primary text-sm flex-shrink-0 mt-0.5" />
                  <span>
                    Every future week, AI runs a fresh analysis on{' '}
                    {competitorUrls.length > 0 ? <strong>your competitors</strong> : <strong>your brand brief</strong>}
                    {' '}and generates {cadence} new ad{cadence === 1 ? '' : 's'}.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-time-line text-primary text-sm flex-shrink-0 mt-0.5" />
                  <span>Each post fires at its platform's best engagement hour.</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-pause-circle-line text-primary text-sm flex-shrink-0 mt-0.5" />
                  <span>Pause, edit, or cancel any time from the calendar.</span>
                </li>
              </ul>
              {competitorUrls.length === 0 && (
                <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
                  <i className="ri-lightbulb-flash-line mr-1" />
                  Tip: add competitors in My Competitors for richer ads.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2 mb-3">
              <i className="ri-error-warning-line mt-0.5" />
              <div className="flex-1">{error}</div>
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <><i className="ri-loader-4-line animate-spin" /> Filling your calendar…</>
            ) : source === 'generate' ? (
              <>
                <i className="ri-flight-takeoff-line" />
                Schedule {tilesPreview} ad{tilesPreview === 1 ? '' : 's'}
              </>
            ) : (
              <><i className="ri-flight-takeoff-line" /> Schedule {tilesPreview} post{tilesPreview === 1 ? '' : 's'}</>
            )}
          </button>
        </Section>
      </div>
    </AppLayoutV2>
  );
}

// Grayed, non-interactive card used by the read-only pilot detail view — the
// same visual shape as the wizard's option cards, muted to read as view-only.
function ReadOnlyCard({ title, sub, icon }: { title: string; sub?: string; icon?: string }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-surface-dark/30 bg-surface/40 px-4 py-3 opacity-90">
      {icon && (
        <div className="w-9 h-9 rounded-lg bg-surface-dark/20 flex items-center justify-center text-dark-muted shrink-0">
          <i className={icon} />
        </div>
      )}
      <div className="min-w-0">
        <div className="font-display font-bold text-dark text-base">{title}</div>
        {sub && <div className="text-xs text-dark-muted mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

// Small numbered-section wrapper used to give the single-screen wizard
// the visual cadence of a step flow without actually breaking it into
// pages.
function Section({
  number,
  title,
  icon,
  children,
}: {
  number: number;
  title: string;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-5 rounded-2xl glass-stroke bg-white/85 p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
          {number}
        </div>
        <i className={`${icon} text-primary text-lg`} />
        <h2 className="font-display font-bold text-dark text-base">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function SourceCard({
  active,
  onClick,
  icon,
  title,
  body,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  body: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all flex items-start gap-3 cursor-pointer ${
        active
          ? 'border-primary bg-primary/5'
          : disabled
            ? 'border-surface-dark/20 bg-surface/30 cursor-not-allowed opacity-70'
            : 'border-surface-dark/30 bg-white hover:border-primary/40'
      }`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
        active ? 'bg-primary text-white' : 'bg-primary/10 text-primary'
      }`}>
        <i className={`${icon} text-lg`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-display font-bold text-dark text-sm">{title}</div>
        <div className="text-xs text-dark-muted mt-0.5">{body}</div>
      </div>
      {active && (
        <i className="ri-check-line text-primary text-lg flex-shrink-0" />
      )}
    </button>
  );
}
